import { Lock, ShieldAlert, ShieldCheck, Wifi, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { tx } from '../../i18n';
import { CATEGORY_ORDER, categoryShort, isUnrated, orderedCategories, tileNote } from '../../lib/report';
import { statusColour, statusFill, statusIcon, statusLabel, statusOfCategory } from '../../lib/status';
import type { Status } from '../../lib/status';
import type { AnalysisSummaryResponse, CategoryDetail, CategoryName } from '../../types/api';

const ICONS: Record<CategoryName, LucideIcon> = {
  SAFETY: ShieldAlert,
  COMFORT: Zap,
  CONNECTIVITY: Wifi,
  COMPLIANCE: ShieldCheck,
};

/**
 * The verdict word for a tile: what this score means for this category, not just how big it is.
 *
 * <p>"Connectivity 84" asks the reader to hold a scale in their head. "Good connectivity" does
 * not, which is the entire point of putting a word next to every number on this page.
 */
function tileVerdict(category: CategoryName, status: Status): string {
  if (status === 'unknown') {
    return tx('No data', 'Veri yok', 'Brak danych', 'Sem dados', 'Sin datos');
  }
  switch (category) {
    case 'COMFORT':
      return status === 'good'
        ? tx('Warm and cheap to run', 'Sıcak ve ekonomik', 'Ciepłe i tanie w utrzymaniu', 'Quente e barato de manter', 'Cálida y barata de mantener')
        : status === 'attention'
          ? tx('Comfort needs attention', 'Konfor dikkat istiyor', 'Komfort wymaga uwagi', 'O conforto merece atenção', 'El confort merece atención')
          : tx('Expensive to heat', 'Isıtması pahalı', 'Drogie w ogrzewaniu', 'Caro de aquecer', 'Cara de calentar');
    case 'CONNECTIVITY':
      return status === 'good'
        ? tx('Good connectivity', 'İyi bağlantı', 'Dobre łącze', 'Boa ligação', 'Buena conexión')
        : status === 'attention'
          ? tx('Connectivity is average', 'Bağlantı ortalama', 'Łącze przeciętne', 'Ligação mediana', 'Conexión normal')
          : tx('Slow broadband here', 'Burada internet yavaş', 'Wolny internet', 'Internet lenta aqui', 'Internet lenta aquí');
    case 'SAFETY':
      return status === 'good'
        ? tx('Quiet for the area', 'Bölgesine göre sakin', 'Spokojnie jak na okolicę', 'Sossegado para a zona', 'Tranquilo para la zona')
        : status === 'attention'
          ? tx('Safety needs attention', 'Güvenlik dikkat istiyor', 'Bezpieczeństwo wymaga uwagi', 'A segurança merece atenção', 'La seguridad merece atención')
          : tx('Busy for crime', 'Suç kaydı yoğun', 'Dużo zgłoszeń', 'Muita criminalidade', 'Mucha delincuencia');
    default:
      return statusLabel(status);
  }
}

/**
 * The scores at a glance, above the sections they belong to.
 *
 * <p>With every section folded away, this is what a reader sees after the verdict: a number per
 * category, the word that grades it, a bar each, and the one fact behind it. Pressing one opens
 * its section and scrolls there, which is why a tile is a link to the section's id rather than a
 * button - it works from the keyboard, on a middle click, and when the page is shared at that
 * anchor.
 */
export default function ScoreTiles({ categories }: { categories: CategoryDetail[] }) {
  return (
    <div className="score-tiles" data-tour="score-tiles">
      {orderedCategories(categories).map((category) => {
        const Icon = ICONS[category.category] ?? ShieldCheck;
        const status = statusOfCategory(category);
        const StatusIcon = statusIcon(status);
        const unrated = isUnrated(category);
        const colour = statusColour(status);
        return (
          <a
            key={category.category}
            href={`#section-${category.category.toLowerCase()}`}
            className={`score-tile score-tile--${status}`}
          >
            <span className="score-tile__head">
              <span className="score-tile__name">
                <Icon size={16} aria-hidden="true" />
                {categoryShort(category.category)}
              </span>
              <span className="score-tile__score" style={{ color: colour }}>
                {unrated ? '–' : category.score}
              </span>
            </span>
            <span className="meter" aria-hidden="true">
              <span style={{ width: unrated ? '0%' : `${category.score}%`, background: statusFill(status) }} />
            </span>
            <span className="score-tile__verdict" style={{ color: colour }}>
              <StatusIcon size={14} aria-hidden="true" />
              {tileVerdict(category.category, status)}
            </span>
            <span className="score-tile__note">{tileNote(category)}</span>
          </a>
        );
      })}
    </div>
  );
}

/**
 * The free tier's stand-in for the sections: a sentence per category and no number, because none
 * is sent until the report is unlocked.
 */
export function LockedTiles({ categories }: { categories: AnalysisSummaryResponse['categorySummaries'] }) {
  const lookup = new Map(categories.map((category) => [category.category, category]));
  return (
    <div className="score-tiles">
      {CATEGORY_ORDER.map((name) => {
        const category = lookup.get(name);
        if (!category) {
          return null;
        }
        const Icon = ICONS[name] ?? ShieldCheck;
        return (
          <div key={name} className="score-tile score-tile--locked">
            <span className="score-tile__head">
              <span className="score-tile__name">
                <Icon size={16} aria-hidden="true" />
                {categoryShort(name)}
              </span>
              <Lock size={15} aria-hidden="true" />
            </span>
            <span className="score-tile__note">{category.summary}</span>
          </div>
        );
      })}
    </div>
  );
}
